/**
 * Base API Client
 * 
 * This module provides a base API client with JWT token management,
 * automatic token refresh, and comprehensive error handling.
 */

import { jwtDecode } from 'jwt-decode';

// Get public user email from environment (configured to match backend PUBLIC_USER_EMAIL)
const PUBLIC_USER_EMAIL = process.env.REACT_APP_PUBLIC_USER_EMAIL || null;

// Helper to get user email from token
const getUserEmailFromToken = (token: string | null): string | null => {
  if (!token) return null;
  try {
    const decoded = jwtDecode<{ email?: string }>(token);
    return decoded.email || null;
  } catch {
    return null;
  }
};

// Helper to check if current user is the public user
const isPublicUser = (token: string | null): boolean => {
  if (!PUBLIC_USER_EMAIL || !token) return false;
  const userEmail = getUserEmailFromToken(token);
  return userEmail === PUBLIC_USER_EMAIL;
};

// Types
interface ApiConfig {
  baseURL: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
}

interface TokenKeys {
  ACCESS_TOKEN: string;
  REFRESH_TOKEN: string;
  TOKEN_EXPIRY: string;
}

interface RequestOptions extends RequestInit {
  method?: string;
  headers?: HeadersInit;
  body?: BodyInit | null;
  signal?: AbortSignal;
}

interface TokenChangeEvent {
  accessToken: string | null;
  refreshToken: string | null;
}

interface JwtPayload {
  sub: string;
  email: string;
  iat: number;
  jti: string;
  first_name: string;
  last_name: string;
  company: string;
  exp: number;
  type: string;
  [key: string]: any;
}

interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

// API Configuration
const API_CONFIG: ApiConfig = {
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  timeout: 30000, // 30 seconds
  retryAttempts: 3,
  retryDelay: 1000, // 1 second
};

// Token storage keys
const TOKEN_KEYS: TokenKeys = {
  ACCESS_TOKEN: 'auth_access_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  TOKEN_EXPIRY: 'auth_token_expiry',
};

/**
 * API Client Class
 */
class ApiClient {
  private config: ApiConfig;
  private baseURL: string;
  private timeout: number;
  private retryAttempts: number;
  private retryDelay: number;
  private requestInterceptors: Array<(config: RequestOptions) => RequestOptions> = [];
  private responseInterceptors: Array<(response: Response) => Response> = [];
  private isRefreshing: boolean = false;
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (error: any) => void;
  }> = [];
  private tokenChangeListeners: Set<(tokens: TokenChangeEvent) => void> = new Set();

  constructor(config: Partial<ApiConfig> = {}) {
    this.config = { ...API_CONFIG, ...config };
    this.baseURL = this.config.baseURL;
    this.timeout = this.config.timeout;
    this.retryAttempts = this.config.retryAttempts;
    this.retryDelay = this.config.retryDelay;
  }

  /**
   * Add request interceptor
   */
  addRequestInterceptor(interceptor: (config: RequestOptions) => RequestOptions): void {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(interceptor: (response: Response) => Response): void {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Apply request interceptors
   */
  private applyRequestInterceptors(config: RequestOptions): RequestOptions {
    return this.requestInterceptors.reduce((acc, interceptor) => interceptor(acc), config);
  }

  /**
   * Apply response interceptors
   */
  private async applyResponseInterceptors(response: Response): Promise<Response> {
    let modifiedResponse = response;
    for (const interceptor of this.responseInterceptors) {
      modifiedResponse = interceptor(modifiedResponse);
    }
    return modifiedResponse;
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
    const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
    if (SHOULD_DEBUG) {
      console.log('ApiClient - getAccessToken called, token:', token ? token.substring(0, 20) + '...' : 'No token');
    }
    return token;
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
  }

  /**
   * Set tokens in storage
   */
  setTokens(accessToken: string | null, refreshToken: string | null): void {
    const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
    if (SHOULD_DEBUG) {
      console.log('=== ApiClient - setTokens called ===');
      console.log('ApiClient - Parameters:', {
        accessToken: accessToken ? accessToken.substring(0, 20) + '...' : 'No access token',
        refreshToken: refreshToken ? refreshToken.substring(0, 20) + '...' : 'No refresh token',
        accessTokenType: typeof accessToken,
        refreshTokenType: typeof refreshToken
      });
      console.log('ApiClient - TOKEN_KEYS:', TOKEN_KEYS);
      console.log('ApiClient - localStorage before:', Object.keys(localStorage));
    }
 
    if (accessToken) {
      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
      // Calculate expiry time (24 hours from now)
      const expiryTime = Date.now() + (24 * 60 * 60 * 1000);
      localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, expiryTime.toString());
    }
    
    if (refreshToken) {
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
    }
    
    if (SHOULD_DEBUG) {
      console.log('ApiClient - localStorage after:', Object.keys(localStorage));
      console.log('ApiClient - Final localStorage values:', {
        accessToken: localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN) ? 'STORED' : 'NOT STORED',
        refreshToken: localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN) ? 'STORED' : 'NOT STORED',
        expiry: localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY) ? 'STORED' : 'NOT STORED'
      });
    }
    
    this.notifyTokenChange({ accessToken, refreshToken });
  }

  /**
   * Clear all tokens (unless user is public user)
   */
  clearTokens(): void {
    const token = this.getAccessToken();
    // Don't clear tokens for public user - they should remain logged in
    if (token && isPublicUser(token)) {
      const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
      if (SHOULD_DEBUG) console.log('ApiClient - Public user detected, preserving tokens');
      return;
    }
    
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRY);
    this.notifyTokenChange({ accessToken: null, refreshToken: null });
  }

  /**
   * Notify token change listeners
   */
  private notifyTokenChange(tokens: TokenChangeEvent): void {
    this.tokenChangeListeners.forEach(listener => listener(tokens));
  }

  /**
   * Add token change listener
   */
  addTokenChangeListener(listener: (tokens: TokenChangeEvent) => void): void {
    this.tokenChangeListeners.add(listener);
  }

  /**
   * Remove token change listener
   */
  removeTokenChangeListener(listener: (tokens: TokenChangeEvent) => void): void {
    this.tokenChangeListeners.delete(listener);
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string | null = null): boolean {
    const tokenToCheck = token || this.getAccessToken();
    const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
    if (SHOULD_DEBUG) {
      console.log('ApiClient - isTokenExpired check:', {
        token: tokenToCheck ? tokenToCheck.substring(0, 20) + '...' : 'No token',
        hasToken: !!tokenToCheck
      });
    }
    
    if (!tokenToCheck) {
      return true;
    }
    
    try {
      const decoded = jwtDecode<JwtPayload>(tokenToCheck);
      const now = Date.now();
      const expiry = decoded.exp * 1000;
      // Remove the 5-minute buffer for now to see if that's causing issues
      const isExpired = now >= expiry;
      if (SHOULD_DEBUG) {
        console.log('ApiClient - Token expiry check:', {
          now: new Date(now).toISOString(),
          expiry: new Date(expiry).toISOString(),
          isExpired: isExpired,
          timeUntilExpiry: Math.round((expiry - now) / 1000 / 60) + ' minutes',
          bufferRemoved: true
        });
      }
      
      return isExpired;
    } catch (error) {
      if (SHOULD_DEBUG) {
        console.warn('ApiClient - Failed to decode token:', error);
        console.log('ApiClient - Token that failed to decode:', tokenToCheck);
      }
      return true;
    }
  }

  /**
   * Check if token needs refresh
   */
  needsRefresh(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }
    
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const now = Date.now();
      const expiry = decoded.exp * 1000;
      const timeUntilExpiry = expiry - now;
      const refreshThreshold = 15 * 60 * 1000; // 15 minutes
      const needsRefresh = timeUntilExpiry <= refreshThreshold;
      const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
      if (SHOULD_DEBUG) {
        console.log('ApiClient - needsRefresh check:', {
          tokenExpiry: new Date(expiry).toISOString(),
          currentTime: new Date(now).toISOString(),
          timeUntilExpiry: Math.round(timeUntilExpiry / 1000 / 60) + ' minutes',
          refreshThreshold: Math.round(refreshThreshold / 1000 / 60) + ' minutes',
          needsRefresh: needsRefresh,
          calculation: `${timeUntilExpiry} <= ${refreshThreshold} = ${needsRefresh}`
        });
      }
      
      // Refresh if token expires within 15 minutes
      return needsRefresh;
    } catch (error) {
      const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
      if (SHOULD_DEBUG) console.warn('Failed to decode token for refresh check:', error);
      return true;
    }
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken(): Promise<RefreshTokenResponse> {
    const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
    if (SHOULD_DEBUG) console.log('ApiClient - refreshAccessToken called');
    
    if (this.isRefreshing) {
      if (SHOULD_DEBUG) console.log('ApiClient - Already refreshing, queuing request');
      // If already refreshing, wait for the current refresh to complete
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      console.log('ApiClient - No refresh token available');
      throw new Error('No refresh token available');
    }

    if (SHOULD_DEBUG) console.log('ApiClient - Starting token refresh with refresh token:', refreshToken.substring(0, 20) + '...');
    this.isRefreshing = true;

    try {
      const response = await fetch(`${this.baseURL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (SHOULD_DEBUG) console.log('ApiClient - Refresh response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (SHOULD_DEBUG) console.error('ApiClient - Token refresh failed:', errorData);
        throw new Error(errorData.detail || 'Token refresh failed');
      }

      const data = await response.json();
      const { access_token, refresh_token } = data;

      if (SHOULD_DEBUG) console.log('ApiClient - Token refresh successful, updating tokens');
      // Update stored tokens
      this.setTokens(access_token, refresh_token || refreshToken);

      // Process failed queue
      this.processQueue(null, { 
        access_token, 
        refresh_token, 
        token_type: 'Bearer',
        expires_in: 3600 
      });

      return { 
        access_token, 
        refresh_token, 
        token_type: 'Bearer',
        expires_in: 3600 
      };
    } catch (error) {
      if (SHOULD_DEBUG) console.error('ApiClient - Token refresh error:', error);
      // Clear tokens on refresh failure to trigger logout (unless public user)
      const currentToken = this.getAccessToken();
      if (!isPublicUser(currentToken)) {
        this.clearTokens();
      } else {
        if (SHOULD_DEBUG) console.log('ApiClient - Public user, preserving tokens despite refresh failure');
      }
      // Process failed queue with error
      this.processQueue(error, null);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Process failed request queue
   */
  private processQueue(error: any, tokens: RefreshTokenResponse | null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(tokens);
      }
    });
    
    this.failedQueue = [];
  }

  /**
   * Make HTTP request
   */
  async request(url: string, options: RequestOptions = {}, retryCount: number = 0): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      // Apply request interceptors
      const modifiedConfig = this.applyRequestInterceptors({
        ...options,
        signal: controller.signal,
      });

      const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
      if (SHOULD_DEBUG) {
        console.log('API Request:', {
          method: modifiedConfig.method || 'GET',
          headers: modifiedConfig.headers,
        });
      }

      const response = await fetch(`${this.baseURL}${url}`, {
        ...modifiedConfig,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Apply response interceptors
      const modifiedResponse = await this.applyResponseInterceptors(response);

      return modifiedResponse;
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle network errors with retry
      if (error instanceof Error && (error.name === 'AbortError' || error.name === 'TypeError')) {
        if (retryCount < this.retryAttempts) {
          console.warn(`Request failed, retrying... (${retryCount + 1}/${this.retryAttempts})`);
          await new Promise(resolve => setTimeout(resolve, this.retryDelay * (retryCount + 1)));
          return this.request(url, options, retryCount + 1);
        }
      }

      throw error;
    }
  }

  /**
   * Make authenticated request
   */
  async authenticatedRequest(url: string, options: RequestOptions = {}): Promise<Response> {
    const accessToken = this.getAccessToken();
    
    const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
    if (SHOULD_DEBUG) {
      console.log('ApiClient - authenticatedRequest called for:', url);
      console.log('ApiClient - accessToken available:', !!accessToken);
      console.log('ApiClient - accessToken preview:', accessToken ? accessToken.substring(0, 20) + '...' : 'No token');
    }
    
    // Check if token is missing or expired BEFORE making request
    if (!accessToken) {
      if (SHOULD_DEBUG) console.log('ApiClient - No access token available, clearing tokens and throwing error');
      this.clearTokens();
      throw new Error('Authentication failed - please log in again');
    }

    // Check if current user is public user
    const isPublic = isPublicUser(accessToken);

    // Check if token is expired (not just if it needs refresh)
    const isExpired = this.isTokenExpired(accessToken);
    if (SHOULD_DEBUG) {
      console.log('ApiClient - Token expiration check:', { isExpired, isPublicUser: isPublic });
    }

    // Check if token needs refresh (within 15 minutes of expiry)
    const needsRefresh = this.needsRefresh();
    if (SHOULD_DEBUG) console.log('ApiClient - Token refresh check result:', needsRefresh);
    
    // If token is expired OR needs refresh, try to refresh it
    if (isExpired || needsRefresh) {
      if (SHOULD_DEBUG) {
        console.log('ApiClient - Token expired or needs refresh:', { isExpired, needsRefresh, isPublicUser: isPublic, action: 'refreshing...' });
      }
      try {
        await this.refreshAccessToken();
        // After successful refresh, get the new token
        const newAccessToken = this.getAccessToken();
        if (!newAccessToken) {
          if (SHOULD_DEBUG) console.error('ApiClient - Token refresh succeeded but no new token available');
          // For public user, allow continuing with expired token
          if (!isPublic) {
            this.clearTokens();
            throw new Error('Authentication failed - please log in again');
          }
          if (SHOULD_DEBUG) console.log('ApiClient - Public user, allowing request with expired token');
        }
      } catch (error) {
        if (SHOULD_DEBUG) console.error('ApiClient - Token refresh failed:', error);
        // For public user, allow continuing with expired token
        if (!isPublic) {
          this.clearTokens();
          throw new Error('Authentication failed - please log in again');
        }
        if (SHOULD_DEBUG) console.log('ApiClient - Public user, allowing request despite refresh failure');
      }
    }

    const finalToken = this.getAccessToken();
    if (!finalToken) {
      if (SHOULD_DEBUG) console.error('ApiClient - No token available after refresh attempt');
      // For public user, this shouldn't happen but be lenient
      if (!isPublic) {
        this.clearTokens();
        throw new Error('Authentication failed - please log in again');
      }
      if (SHOULD_DEBUG) console.log('ApiClient - Public user, allowing request without token');
    }
    
    if (SHOULD_DEBUG) console.log('ApiClient - Using final token:', finalToken ? finalToken.substring(0, 20) + '...' : 'No token');
    
    const config: RequestOptions = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${finalToken}`,
      },
    };

    try {
      const response = await this.request(url, config);
      
      // Handle 401 Unauthorized - even after refresh, if we still get 401, logout (unless public user)
      if (response.status === 401) {
        const currentToken = this.getAccessToken();
        const isPublic = isPublicUser(currentToken);
        if (SHOULD_DEBUG) console.log('ApiClient - Received 401 after refresh attempt', { isPublicUser: isPublic });
        // Don't try to refresh again - we already tried, this means auth is invalid
        // But for public user, don't clear tokens
        if (!isPublic) {
          this.clearTokens();
          throw new Error('Authentication failed - please log in again');
        }
        // For public user, just throw the error without clearing tokens
        if (SHOULD_DEBUG) console.log('ApiClient - Public user 401, preserving session');
        throw new Error('Authentication failed');
      }
      
      return response;
    } catch (error) {
      const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
      if (SHOULD_DEBUG) console.error('ApiClient - Request failed:', error);
      
      // If the error is about authentication, ensure tokens are cleared (unless public user)
      if (error instanceof Error && error.message.includes('Authentication failed')) {
        const currentToken = this.getAccessToken();
        if (!isPublicUser(currentToken)) {
          this.clearTokens();
        }
      }
      
      throw error;
    }
  }

  /**
   * Authenticated GET request
   */
  async authenticatedGet(url: string, options: RequestOptions = {}): Promise<Response> {
    return this.authenticatedRequest(url, { ...options, method: 'GET' });
  }

  /**
   * Authenticated POST request
   */
  async authenticatedPost(url: string, data: any = null, options: RequestOptions = {}): Promise<Response> {
    return this.authenticatedRequest(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : null,
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Authenticated PUT request
   */
  async authenticatedPut(url: string, data: any = null, options: RequestOptions = {}): Promise<Response> {
    return this.authenticatedRequest(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : null,
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Authenticated DELETE request
   */
  async authenticatedDelete(url: string, options: RequestOptions = {}): Promise<Response> {
    return this.authenticatedRequest(url, { ...options, method: 'DELETE' });
  }

  /**
   * Authenticated PATCH request
   */
  async authenticatedPatch(url: string, data: any = null, options: RequestOptions = {}): Promise<Response> {
    return this.authenticatedRequest(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : null,
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      },
    });
  }
}

// Create and export singleton instance
const apiClient = new ApiClient();
export default apiClient;
